import { FileText, Lock, Mail } from 'lucide-react'
import { formatNoteWhen, resolveNoteAuthorName, sanitizeNoteHtml } from '@/lib/utils/noteFormatting'
import { useEffect, useMemo, useState } from 'react'
import { EditNoteModal } from '@/components/notes/EditNoteModal'
import { MentionTextarea } from '@/components/notes/MentionTextarea'
import { primaryEntityActionButtonClass } from '@/components/ui/entityActionButtonClasses'
import { cn } from '@/lib/utils/helpers'
import { showSuccess } from '@/lib/utils/notifications'
import detailStyles from '@/features/reimbursements/styles/reimbursementDetail.module.css'
import { FrequentlyUsedNotesDialog } from '../dialogs/FrequentlyUsedNotesDialog'
import { InternalNotesNotifyDialog } from '../dialogs/InternalNotesNotifyDialog'
import type { ResidentNote } from '../../types/resident'
import type { PublicNoteRow } from '../../hooks/usePublicNotesAndMessages'
import type { ResidentNotesSectionProps } from '../../types'
import {
  getPublicNoteEmailSendTo,
  getPublicNoteRowId,
} from '../../utils/residentPublicNotesHelpers'

const ACTION_BUTTON_CLASS = cn(primaryEntityActionButtonClass, 'whitespace-nowrap')

const FIVE_MINUTES_MS = 5 * 60 * 1000

/** Matches Angular `moment.unix` / `toNoteDate` — unix seconds|ms or parseable date string. */
function toMs(value?: string | number | null): number | null {
  if (value === undefined || value === null || value === '') return null
  if (typeof value === 'number') return value > 1e12 ? value : value * 1000
  const trimmed = String(value).trim()
  if (/^\d{10,13}$/.test(trimmed)) {
    const numeric = Number(trimmed)
    return trimmed.length >= 13 ? numeric : numeric * 1000
  }
  const parsed = Date.parse(trimmed)
  return Number.isNaN(parsed) ? null : parsed
}

/**
 * Liberty Internal notes — Angular `message_insurance` compose with `mentionConfig`
 * (`@` → Liberty user list → `[userid:X]` on save) and `processNotesandMessage` Edit.
 */
export function ResidentNotesSection({
  notes,
  loading,
  hasPolicy: _hasPolicy,
  draftValue,
  onDraftChange,
  noteSaveProcessing = false,
  onSaveInternalNote,
  onNotesUpdated,
  isLibertyRole = false,
  loggedUserId,
}: ResidentNotesSectionProps) {
  const [frequentNotesOpen, setFrequentNotesOpen] = useState(false)
  const [notifyOpen, setNotifyOpen] = useState(false)
  const [editingNote, setEditingNote] = useState<PublicNoteRow | null>(null)
  const [editClock, setEditClock] = useState(0)

  useEffect(() => {
    const id = window.setInterval(() => setEditClock((value) => value + 1), 30_000)
    return () => window.clearInterval(id)
  }, [])

  const appendFrequentlyUsedNotes = (inserted: string) => {
    const trimmed = inserted.trim()
    if (!trimmed) return
    const current = draftValue.trimEnd()
    onDraftChange(current ? `${current}\n${trimmed}` : trimmed)
  }

  // eslint-disable-next-line react-hooks/purity -- intentional: re-evaluate 5-min edit window with editClock
  const now = useMemo(() => Date.now(), [notes, editClock])

  /** Newest-first by created_at (Angular `processNotesandMessage` sort). */
  const sortedNotes = useMemo(() => {
    return [...notes].sort((a, b) => {
      const aMs = toMs((a as { created_at?: string | number }).created_at as string | number | null) ?? 0
      const bMs = toMs((b as { created_at?: string | number }).created_at as string | number | null) ?? 0
      return bMs - aMs
    })
  }, [notes])

  return (
    <div data-testid="resident-notes-section">
      <section
        className={detailStyles.section}
        data-testid="resident-notes-card"
      >
        <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h2 className={detailStyles.sectionTitle}>
            <span className={detailStyles.sectionTitleIcon} aria-hidden="true">
              <Lock className="h-4 w-4" />
            </span>
            Internal Notes (private)
          </h2>
          <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:justify-end sm:gap-2.5">
            <button
              type="button"
              onClick={() => setFrequentNotesOpen(true)}
              data-testid="resident-notes-frequently-used-button"
              className={ACTION_BUTTON_CLASS}
            >
              Frequently Used Notes
            </button>
            <button
              type="button"
              disabled={!draftValue.trim() || noteSaveProcessing}
              onClick={() => setNotifyOpen(true)}
              data-testid="resident-notes-add-save-button"
              className={ACTION_BUTTON_CLASS}
            >
              Save
            </button>
          </div>
        </div>
        <MentionTextarea
          value={draftValue}
          onChange={onDraftChange}
          onTaggingAdd={() => undefined}
          rows={5}
          disabled={noteSaveProcessing}
          className="min-h-[7.5rem] w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-button focus:ring-2 focus:ring-blue-100 disabled:bg-slate-50"
          placeholder="Add note/message (use @ to mention Liberty users)"
          dataTestId="resident-notes-add-textarea"
        />

        <div className="mt-6" data-testid="resident-notes-history-wrap">
          <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-black">
            Notes &amp; Messages
          </h3>
          {loading ? (
            <p className="text-sm text-slate-500" data-testid="resident-notes-loading">
              Loading notes...
            </p>
          ) : sortedNotes.length ? (
            <div
              className="rounded-lg border border-sky-100 bg-sky-50/80 px-2 py-3 sm:px-4"
              data-testid="resident-notes-list"
            >
              <ul className="divide-y divide-sky-100/80">
                {sortedNotes.map((note, index) => {
                  const row = note as PublicNoteRow & ResidentNote
                  // API uses `notes_id` (Angular); also accept note_id / id aliases.
                  const noteRowId = getPublicNoteRowId(row)
                  const noteKey = String(noteRowId ?? index)
                  // Angular: only `type === 'notes'` rows get `is_edit`; insurance messages never do.
                  const isNoteType = row.type === 'notes'
                  const html =
                    row.is_system_html === '1' ||
                    row.is_system_html === true ||
                    row.is_system_html === 1
                  const rawContent = String(row.content ?? row.notes ?? row.message ?? '')
                  const ms = toMs(row.created_at as string | number | undefined)
                  // Angular `processNotesandMessage` is_edit: created < 5 min, Liberty role,
                  // `fk_user_id === loggedUserID`, not system HTML. Messages never get Edit.
                  const noteAuthorId = (row as { fk_user_id?: string | number }).fk_user_id
                  const isOwnNote =
                    loggedUserId !== undefined &&
                    loggedUserId !== null &&
                    noteAuthorId !== undefined &&
                    noteAuthorId !== null &&
                    String(noteAuthorId) === String(loggedUserId)
                  const editable =
                    isNoteType &&
                    !!ms &&
                    now - ms < FIVE_MINUTES_MS &&
                    noteRowId != null &&
                    isLibertyRole &&
                    isOwnNote &&
                    !html

                  const name = resolveNoteAuthorName(row)
                  const emailSendTo = getPublicNoteEmailSendTo(row)

                  return (
                    <li
                      key={noteKey}
                      className="grid grid-cols-1 gap-3 py-3 text-sm text-slate-700 first:pt-1 sm:grid-cols-12 sm:gap-0"
                      data-testid={`resident-notes-row-${noteKey}`}
                    >
                      <div className="flex gap-2 border-sky-200 sm:col-span-3 sm:border-r sm:pr-3">
                        <span className="mt-0.5 shrink-0 text-slate-500" aria-hidden>
                          {isNoteType ? <FileText className="h-4 w-4" /> : <Mail className="h-4 w-4" />}
                        </span>
                        <div className="min-w-0">
                          <div className="text-[13px] font-semibold text-slate-800">{name}</div>
                          <div className="mt-1 text-xs text-slate-600">
                            {formatNoteWhen(row.created_at)}
                          </div>
                          {row.updated_at ? (
                            <div className="mt-0.5 text-xs font-medium text-red-600">
                              Modified {formatNoteWhen(row.updated_at)}
                            </div>
                          ) : null}
                        </div>
                      </div>
                      <div className="min-w-0 break-words border-sky-200 px-0 text-xs font-medium text-slate-800 sm:col-span-2 sm:border-r sm:px-3">
                        {emailSendTo || '\u00A0'}
                      </div>
                      <div className="min-w-0 pl-0 sm:col-span-7 sm:pl-4 sm:pr-2">
                        {html ? (
                          <div
                            className="notes-preline whitespace-pre-wrap break-words text-slate-800 [&_a]:text-[rgb(21_151_255)]"
                            dangerouslySetInnerHTML={{ __html: sanitizeNoteHtml(rawContent) }}
                          />
                        ) : (
                          <span className="whitespace-pre-wrap break-words text-slate-800">{rawContent}</span>
                        )}
                        {editable ? (
                          <button
                            type="button"
                            onClick={() => setEditingNote(row)}
                            data-testid={`resident-notes-row-${noteKey}-edit`}
                            className="ml-3 text-sm font-semibold text-blue-700 underline"
                          >
                            Edit
                          </button>
                        ) : null}
                      </div>
                    </li>
                  )
                })}
              </ul>
            </div>
          ) : (
            <div
              className="rounded-lg border border-sky-100 bg-sky-50/80 px-4 py-6 text-center text-sm text-slate-500"
              data-testid="resident-notes-empty"
            >
              No notes or messages.
            </div>
          )}
        </div>
      </section>

      <FrequentlyUsedNotesDialog
        isOpen={frequentNotesOpen}
        onClose={() => setFrequentNotesOpen(false)}
        onInsert={appendFrequentlyUsedNotes}
      />

      <InternalNotesNotifyDialog
        isOpen={notifyOpen}
        processing={noteSaveProcessing}
        onClose={() => setNotifyOpen(false)}
        onSubmit={(selection) => {
          const trimmed = draftValue.trim()
          if (!trimmed) return
          setNotifyOpen(false)
          void Promise.resolve(onSaveInternalNote(trimmed, selection)).catch(() => undefined)
        }}
      />

      <EditNoteModal
        isOpen={!!editingNote}
        notesId={editingNote ? getPublicNoteRowId(editingNote) : null}
        initialNotes={String(editingNote?.content ?? editingNote?.notes ?? '')}
        onClose={() => setEditingNote(null)}
        onSaved={() => {
          showSuccess('Note Edited Successfully!')
          setEditingNote(null)
          void onNotesUpdated?.()
        }}
        testIdPrefix="resident-internal-notes-edit"
      />
    </div>
  )
}
